import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, Image, LoaderCircle, Play, Volume2, X } from 'lucide-react';
import { CommentAttachment, commentsApi } from './api';

export interface QueuedCommentFile { id: string; file: File; voice?: boolean; duration?: number; }
export const commentFileSize = (size: number) => size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;
export const commentFileLimit = (file: File) => (file.type.startsWith('image/') ? 20 : file.type.startsWith('video/') ? 50 : 25) * 1024 * 1024;

export function QueuedAttachment({ item, onRemove, disabled }: { item: QueuedCommentFile; onRemove: () => void; disabled?: boolean }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!item.file.type.startsWith('image/') && !item.file.type.startsWith('audio/')) return;
    const value = URL.createObjectURL(item.file); setUrl(value);
    return () => URL.revokeObjectURL(value);
  }, [item.file]);
  return <div className="lato-comment-queued">
    {item.file.type.startsWith('image/') && url ? <img src={url} alt={item.file.name} /> : item.voice || item.file.type.startsWith('audio/') ? <Volume2 size={18} /> : item.file.type.startsWith('video/') ? <Play size={18} /> : <FileText size={18} />}
    <div className="lato-comment-queued-info"><span>{item.voice ? 'Voice message' : item.file.name}</span><small>{item.voice && item.duration ? `${Math.max(1, Math.round(item.duration / 1000))} sec · ` : ''}{commentFileSize(item.file.size)}</small>
      {item.file.type.startsWith('audio/') && url ? <audio controls preload="metadata" src={url} aria-label={`Preview ${item.file.name}`} /> : null}
    </div>
    <button type="button" className="lato-comment-icon" disabled={disabled} aria-label={`Remove ${item.file.name}`} onClick={onRemove}><X size={16} /></button>
  </div>;
}

/** Private attachments are fetched with auth only on demand; blob URLs never persist. */
export function CommentMedia({ attachment }: { attachment: CommentAttachment }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const urlRef = useRef('');
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; controller.current?.abort(); if (urlRef.current) URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }; }, []);
  const load = async (download = false) => {
    if (loading) return;
    setLoading(true); setError('');
    try {
      let source = urlRef.current;
      if (!source) {
        controller.current = new AbortController();
        const blob = await commentsApi.attachmentBlob(attachment.id, controller.current.signal);
        if (!alive.current) return;
        source = URL.createObjectURL(blob); urlRef.current = source;
      }
      if (download || attachment.kind === 'file') {
        const link = document.createElement('a'); link.href = source; link.download = attachment.name; link.click();
      } else setUrl(source);
    } catch (err) { if (alive.current) setError(err instanceof Error ? err.message : 'Could not open attachment'); }
    finally { if (alive.current) setLoading(false); }
  };
  const Icon = attachment.kind === 'image' ? Image : attachment.kind === 'video' ? Play : attachment.kind === 'audio' ? Volume2 : FileText;
  return <div className="lato-comment-media">
    {url && attachment.kind === 'image' ? <a href={url} target="_blank" rel="noreferrer" aria-label={`Open ${attachment.name} in a new tab`}><img src={url} alt={attachment.name} /></a> : null}
    {url && attachment.kind === 'video' ? <video controls playsInline preload="metadata" src={url} aria-label={attachment.name} /> : null}
    {url && attachment.kind === 'audio' ? <audio controls preload="metadata" src={url} aria-label={attachment.name} /> : null}
    <div className="lato-comment-media-label">
      <button type="button" onClick={() => void load()} disabled={loading} aria-label={`${attachment.kind === 'file' ? 'Download' : 'Open'} ${attachment.name}`}>
        {loading ? <LoaderCircle className="lato-comment-spin" size={19} /> : <Icon size={19} />}<span>{attachment.name}<small>{commentFileSize(attachment.size)}{!url && attachment.kind !== 'file' ? ' · Tap to preview' : ''}</small></span>
      </button>
      {attachment.kind !== 'file' ? <button type="button" className="lato-comment-icon" onClick={() => void load(true)} disabled={loading} aria-label={`Download ${attachment.name}`}><Download size={16} /></button> : null}
    </div>
    {error ? <p className="lato-comment-error" role="alert">{error}</p> : null}
  </div>;
}
